import React, { useContext, useEffect, useState } from 'react';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { CustomControls} from "../controls/CustomControls";
import './MapView.css';
import MapEffect from './MapEffect';
import OpacitySliderContext from '../../context/opacitySliderContext';
import * as Constants from "../../constants"
import parameterContext from '../../context/parameterContext';
import { BannerInfo } from '../bannerInfo/BannerInfo';
import { DivIcon, Point } from 'leaflet';
import MarkerIcon from '../../assets/img/controls/icons/marker.svg'
import { MapLogo } from '../mapLogo/mapLogo';
import { useSearchParams } from 'react-router-dom';
import { OpacitySlider, Opacity } from '../opacitySlider/OpacitySlider';
import { LabelLayer } from '../layer/LabelLayer';
import { TimestampProvider } from '../../providers/TimestampProvider';



const Map: React.FC = () => {
    const parameters = useContext(parameterContext);
    let locationBrowser: any = null;
    const [center, setCenter] = useState<[number, number]>(Constants.MAP_CENTER ?? [0, 0]);
    const [searchParams, setSearchParams] = useSearchParams();
    const locationDetect = searchParams.get('locationDetect');
    let opacitySetting: Opacity = {
        visible: false,
        value: 0,
        x: 0,
        y: 0
    };
    if ( parameters.opacityValue) {
        opacitySetting.value = parameters.opacityValue;
    }

    const  getWindowSize = () =>{
        let myWidth = 0, myHeight = 0;
        
        if(parameters.autosize_onload_enabled){
            myWidth = window.innerWidth;
            myHeight = window.innerHeight;
            
        }else{
            if (parameters.w){
                myWidth = parameters.w;
            }

            if (parameters.h){
                myHeight = parameters.h;
            }
        }
        
        if (myWidth == 0 || myHeight == 0) {
            if (document.documentElement && ( document.documentElement.clientWidth || document.documentElement.clientHeight )) {
                //IE 6+ in 'standards compliant mode'
                myWidth = document.documentElement.clientWidth;
                myHeight = document.documentElement.clientHeight;
            } else if (document.body && ( document.body.clientWidth || document.body.clientHeight )) {
                //IE 4 compatible
                myWidth = document.body.clientWidth;
                myHeight = document.body.clientHeight;
            }

        }

        return {width: myWidth, height: myHeight};
    }
    const [windowSize, setWindowSize] = useState(getWindowSize());
    const [opacity, setOpacity] = useState<Opacity>(opacitySetting);
    const [zoom, setZoom] = useState<number>(parameters.z ?? 5);

    const doDetectLocation = (): Promise<{lat:number,long:number} | false> => {
        return new Promise((resolve,reject)=>{
            if (!navigator.geolocation) {
                reject(false);
            }
            navigator.geolocation.getCurrentPosition((position)=>{
                if (locationBrowser) {
                    reject(false)
                }
                locationBrowser = position;
                const lat = position.coords.latitude;
                const long = position.coords.longitude;

                resolve({lat, long}) ;

            }, ()=>{
                reject(false)
            }, {
                timeout: (30 * 1000),
                maximumAge: 0, //(1000 * 60 * 15),
                enableHighAccuracy: true
            })
        })
    }

    const getCenterPosition = async () => {

        let latitude = 0;
        let longitude = 0;
        
        if (parameters.lat) {
            latitude = parameters.lat;
        }
        if (parameters.lng) {
            longitude = parameters.lng;
        }

        if(latitude && longitude){
            setCenter([latitude,longitude])
        }
        
        if (parameters.detect_location_enabled || (locationDetect && locationDetect == '1')) {
            try{
                const position = await doDetectLocation();
                if (position){
                    latitude = position.lat;
                    longitude = position.long;
                    setCenter([latitude,longitude])
                    return;
                }
            }catch (e){
                console.log('Error getting the data')
            }
        }
        

    }
    const handleResize = () => {
        const size=getWindowSize();
        setWindowSize(size);
    };
   
    useEffect(() => {

        getCenterPosition();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const icon = new DivIcon({
        iconSize:  new Point(40, 20),
        iconAnchor: new Point(20, 20),
        html: '<img src="' + MarkerIcon + '" width="20" height="35" />',
        className: 'mapfit-div-icon-custom',
    });

    return (
        <OpacitySliderContext.Provider value={  {opacity,setOpacity} } >
            { opacity.visible && <OpacitySlider/> }
            <TimestampProvider>
                <div className="map-container" style={ { height: windowSize.height+'px', width: windowSize.width+'px' } }>
                    <MapContainer center={ center } zoom={ zoom } trackResize={true} style={ { height: '100%', width:'100%' } }
                                zoomControl={ false } attributionControl={ false } minZoom={ parameters.minZoom ?? undefined }>
                        {
                            (typeof (parameters.basemapValue) != 'undefined' && parameters.basemapValue == 'ocean') ?
                                <>
                                    <TileLayer
                                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
                                        attribution='&copy; <a href="https://doc.arcgis.com/en/arcgis-online/reference/tile-layers.htm" target="_blank">Esri</a>'
                                        maxZoom={ 13 } zIndex={ 0 }
                                    />
                                    <TileLayer
                                        url="https://tiles.stadiamaps.com/tiles/stamen_terrain_lines/{z}/{x}/{y}{r}.png"
                                        minZoom={ 0 } maxZoom={ 18 } zIndex={ 1 }
                                        attribution=''
                                    />
                                </> : (typeof (parameters.basemapValue) != 'undefined' && parameters.basemapValue == 'stadia') ?
                                    <>
                                        <TileLayer
                                            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>'
                                            subdomains='abcd'
                                            maxZoom={ 18 }
                                            id='osm.mapnik'
                                            zIndex={ 0 }
                                        />
                                        <TileLayer
                                            url="https://tiles.stadiamaps.com/tiles/stamen_terrain_lines/{z}/{x}/{y}{r}.png"
                                            minZoom={ 0 } maxZoom={ 18 } zIndex={ 1 }
                                            attribution=''
                                        />


                                    </> :
                                    <TileLayer
                                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>'
                                        subdomains='abcd'
                                        maxZoom={ 18 }
                                        id='osm.mapnik'
                                        zIndex={ 0 }
                                    />
                        }
                        
                        <LabelLayer/>
                        { parameters?.drop_center_pin && <Marker position={center} icon={icon}></Marker>}
                        <CustomControls/>
                        <MapEffect center={ center }/>
                        <CustomTileLayer/>
                        <BannerInfo/>
                    </MapContainer>
                    {!parameters.hide_logo && <MapLogo/>}
                </div>
            </TimestampProvider>
        </OpacitySliderContext.Provider>
    );
};

export const CustomTileLayer: React.FC = () => {
    return (
        <div className="copyrights">
            <div className="copyright">&copy; <a href="https://zoomradar.com/" target={'_blank'} >ZoomRadar</a></div>
            |
            <div className="copyright">&copy; <a href="https://www.openstreetmap.org/copyright"  target={'_blank'} >OpenStreetMap</a></div>
        </div>
    );
};

export default Map;
